import { isEmptyValue } from './LegacyModeStrategy.js';

/** Служебные `kind` пунктов, которые виджет подмешивает в результаты поиска. */
const MIXED_ITEM_USE_TEXT = "use-text";
const MIXED_ITEM_CREATE = "create";
/** Идентификатор действия «открыть карточку» у пункта списка. */
const MIXED_ACTION_OPEN = "open-card";
/**
 * Режим смешанного ввода: одно поле, три состояния, два взаимоисключающих ключа модели.
 *
 * Отличия от legacy-режимов, важные при чтении кода:
 *  - источник истины — {@link _state}, а не атрибуты элемента;
 *  - `data-value` хранит ТОЛЬКО `referenceId`; текст в него не зеркалится,
 *    иначе состояния `text` и `reference` неразличимы при повторной инициализации;
 *  - {@link setValue} возвращает `false` — см. комментарий у метода.
 */
class MixedModeStrategy {
    constructor(context) {
        this.context = context;
        this.mode = "single";
        this._state = { type: "empty" };
        this._state = this._readInitialState();
        this._syncElement();
    }
    /** Текущее состояние (копия — наружу мутировать нечего). */
    getState() {
        return { ...this._state };
    }
    /** Установить состояние программно и синхронизировать поле. */
    setState(value) {
        this._state = value;
        this._syncElement();
    }
    /**
     * Ручная правка текста в поле.
     *
     * Если поле было ссылкой и оператор изменил название — связь со справочником рвётся:
     * сохранять GUID под изменённым названием нельзя.
     */
    onChange() {
        const text = this.context.element.value.trim();
        if (this._state.type === "reference") {
            const shown = this._state.displayName ?? this._state.id;
            if (text === shown)
                return;
        }
        else if (this._state.type === "text" && this._state.text === text) {
            return;
        }
        else if (this._state.type === "empty" && text === "") {
            return;
        }
        this._state = text ? { type: "text", text } : { type: "empty" };
        this._syncElement();
        this.context.emitComboChange();
    }
    /** Выбор пункта списка: обычная запись, «использовать как текст» или «создать». */
    onSelect(item, index) {
        if (item.kind === MIXED_ITEM_USE_TEXT) {
            const text = (item.text ?? "").trim();
            this._state = text ? { type: "text", text } : { type: "empty" };
            this._syncElement();
            this.context.emitComboChange({ item, index });
            this.context.element.dispatchEvent(new Event("change", { bubbles: true }));
            this.context.refocusCaretToEnd();
            return;
        }
        if (item.kind === MIXED_ITEM_CREATE) {
            // Состояние меняется только после успешного ответа сервера
            this.context.createRecord((item.text ?? "").trim());
            return;
        }
        this._state = {
            type: "reference",
            id: item.key,
            displayName: this.context.displayText(item),
        };
        this._syncElement();
        this.context.emitComboChange({ item, index });
        this.context.element.dispatchEvent(new Event("change", { bubbles: true }));
        this.context.refocusCaretToEnd();
    }
    /** Преобразовать выбранную запись в обычный текст, сохранив её название. */
    convertToText() {
        if (this._state.type !== "reference")
            return;
        const text = this._state.displayName ?? this._state.id;
        this._state = { type: "text", text };
        this._syncElement();
        this.context.emitComboChange();
        this.context.element.dispatchEvent(new Event("change", { bubbles: true }));
    }
    /** Полная очистка значения. */
    clear() {
        this._state = { type: "empty" };
        this._syncElement();
        this.context.emitComboChange();
        this.context.element.dispatchEvent(new Event("change", { bubbles: true }));
    }
    /**
     * Основное значение — GUID для ссылки, текст для текста.
     *
     * ⚠️ Не средство отображения: при `reference` вернёт GUID.
     * Для показа оператору есть {@link getValueTitle}.
     */
    getValue() {
        if (this._state.type === "reference")
            return this._state.id;
        if (this._state.type === "text")
            return this._state.text || null;
        return null;
    }
    /**
     * В mixed скаляр принципиально неоднозначен: по строке нельзя понять,
     * GUID это или текст, а угадывание по формату дало бы ссылку без названия.
     *
     * Поэтому метод возвращает `false` — это предусмотренный контрактом
     * `WithMainValue` путь «не поддерживается, попробуй иначе»,
     * и `setMainValue` штатно уходит в `setValues`, где ключи различимы.
     */
    setValue(_value) {
        return false;
    }
    hasValue() {
        return this._state.type !== "empty";
    }
    /** Человекочитаемое представление — для чипсов фильтра и summary. */
    getValueTitle() {
        if (this._state.type === "reference")
            return this._state.displayName ?? this._state.id;
        if (this._state.type === "text")
            return this._state.text || null;
        return null;
    }
    /**
     * Карта значений формы: заполнен ровно один ключ, второй — `null`.
     *
     * `null` важен: `encodeValues` превратит его в `"dbnull"` (очистить в БД),
     * тогда как `undefined` вообще не попал бы в payload.
     * Пустую строку не отдаём — по ключу, равному `data-key`, `encodeValues`
     * проверяет значение на truthy и иначе перезапишет его из `data-value`.
     */
    getValues() {
        const { textKey, referenceKey } = this.context;
        if (this._state.type === "reference") {
            return { [referenceKey]: this._state.id, [textKey]: null };
        }
        if (this._state.type === "text" && this._state.text !== "") {
            return { [referenceKey]: null, [textKey]: this._state.text };
        }
        return { [referenceKey]: null, [textKey]: null };
    }
    /**
     * Применяет только присутствующие в карте ключи: отсутствующий ключ — «не трогать».
     *
     * Скаляр под ключом текста всегда означает текст, даже если это похоже на GUID:
     * ссылка ставится только явным `referenceKey`.
     *
     * @returns false, если карта не содержит ни одного знакомого ключа.
     */
    setValues(values) {
        const { textKey, referenceKey } = this.context;
        const hasReference = Object.prototype.hasOwnProperty.call(values, referenceKey);
        const hasText = Object.prototype.hasOwnProperty.call(values, textKey);
        if (!hasReference && !hasText)
            return false;
        const rawReference = hasReference ? values[referenceKey] : undefined;
        const rawText = hasText ? values[textKey] : undefined;
        const referenceId = isEmptyValue(rawReference) ? "" : String(rawReference).trim();
        if (referenceId) {
            // Название придёт из data-title, если сервер его отдал
            const title = this.context.element.getAttribute("data-title")?.trim() ?? "";
            const keepName = this._state.type === "reference" && this._state.id === referenceId
                ? this._state.displayName
                : null;
            this._state = { type: "reference", id: referenceId, displayName: keepName || title || null };
            this._syncElement();
            return true;
        }
        if (hasText) {
            const text = isEmptyValue(rawText) ? "" : String(rawText).trim();
            this._state = text ? { type: "text", text } : { type: "empty" };
            this._syncElement();
            return true;
        }
        // Пришёл только referenceKey и он пуст — значит ссылку сняли
        this._state = { type: "empty" };
        this._syncElement();
        return true;
    }
    /**
     * Начальное состояние по разметке. Порядок проверок — часть контракта:
     *  1. непустой `data-value` → reference (название из `data-title`);
     *  2. иначе непустой `value` → text;
     *  3. иначе empty.
     */
    _readInitialState() {
        const id = this.context.element.getAttribute("data-value")?.trim() ?? "";
        if (id) {
            const title = this.context.element.getAttribute("data-title")?.trim() ?? "";
            return { type: "reference", id, displayName: title || null };
        }
        const text = this.context.element.value.trim();
        return text ? { type: "text", text } : { type: "empty" };
    }
    /** Привести поле и атрибуты к текущему состоянию. */
    _syncElement() {
        const element = this.context.element;
        if (this._state.type === "reference") {
            const shown = this._state.displayName ?? this._state.id;
            element.value = shown;
            element.setAttribute("data-value", this._state.id);
            element.setAttribute("data-title", this._state.displayName ?? "");
        }
        else {
            // text и empty: data-value обязан оставаться пустым,
            // иначе следующая инициализация примет текст за ссылку
            element.value = this._state.type === "text" ? this._state.text : "";
            element.setAttribute("data-value", "");
            element.setAttribute("data-title", element.value);
        }
        this.context.onMixedStateChanged?.();
    }
}

export { MIXED_ACTION_OPEN, MIXED_ITEM_CREATE, MIXED_ITEM_USE_TEXT, MixedModeStrategy };
